/**
 * Layer, base-map, and legend-filter state (brief §5.1, §5.3, §6).
 *
 * Persisted to localStorage: the base-map choice, per-layer visibility, and
 * raster opacity. The legend's grade filter is a momentary "show only these"
 * and is not persisted.
 *
 * Base-map ↔ theme: when the UI theme flips, a base map that is *specific to
 * the other theme* (Positron is light-only, Dark Matter is dark-only) is
 * swapped to its pair (§5.1). A theme-agnostic pick (satellite, OSM, relief)
 * is left alone. This replaces the earlier "did the user pick one" flag, which
 * could get stuck and stop the swap from happening.
 */
import { create } from "zustand";

import { getBasemap, THEME_DEFAULT_BASEMAP } from "../map/basemaps";
import type { PlumeGrade } from "../data/types";

const STORAGE_KEY = "kgs.layers";

export type LayerKey = "country" | "regions" | "plumes" | "raster" | "sources" | "aoi";

export type LayerFlags = Record<LayerKey, boolean>;

const DEFAULT_FLAGS: LayerFlags = {
  country: true,
  regions: true,
  plumes: true,
  raster: false, // off until a plume is opened or the user enables it (§5.3)
  sources: true,
  aoi: true,
};

interface Persisted {
  basemapId: string;
  flags: LayerFlags;
  rasterOpacity: number;
}

function read(): Persisted {
  const fallback: Persisted = {
    basemapId: THEME_DEFAULT_BASEMAP.light,
    flags: { ...DEFAULT_FLAGS },
    rasterOpacity: 0.75,
  };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<Persisted>;
    return {
      basemapId: typeof parsed.basemapId === "string" ? parsed.basemapId : fallback.basemapId,
      flags: { ...DEFAULT_FLAGS, ...(parsed.flags ?? {}) },
      rasterOpacity:
        typeof parsed.rasterOpacity === "number" ? parsed.rasterOpacity : fallback.rasterOpacity,
    };
  } catch {
    return fallback;
  }
}

interface LayersState extends Persisted {
  /** Legend quick-filter: grades to show. Empty = show all. Transient. */
  grades: PlumeGrade[];
  setBasemap: (id: string) => void;
  syncBasemapToTheme: (resolved: "light" | "dark") => void;
  toggleLayer: (key: LayerKey) => void;
  setRasterOpacity: (value: number) => void;
  toggleGrade: (grade: PlumeGrade) => void;
  clearGrades: () => void;
}

export const useLayersStore = create<LayersState>((set, get) => {
  const initial = read();

  const persist = () => {
    const { basemapId, flags, rasterOpacity } = get();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ basemapId, flags, rasterOpacity }));
  };

  return {
    ...initial,
    grades: [],

    setBasemap: (id) => {
      set({ basemapId: id });
      persist();
    },
    syncBasemapToTheme: (resolved) => {
      const current = getBasemap(get().basemapId);
      // Only swap when the current base map can't be shown under the new theme.
      if (current.themes === "both" || current.themes === resolved) return;
      set({ basemapId: THEME_DEFAULT_BASEMAP[resolved] });
      persist();
    },
    toggleLayer: (key) => {
      set({ flags: { ...get().flags, [key]: !get().flags[key] } });
      persist();
    },
    setRasterOpacity: (value) => {
      set({ rasterOpacity: value });
      persist();
    },
    toggleGrade: (grade) => {
      const grades = get().grades;
      set({
        grades: grades.includes(grade)
          ? grades.filter((g) => g !== grade)
          : [...grades, grade],
      });
    },
    clearGrades: () => set({ grades: [] }),
  };
});
